/**
 * Kết quả 1 câu Phát âm sau khi nộp bài (plan 2026-10-03): điểm (thang 10, §13),
 * những gì AI nghe được, nhận xét, nghe lại bản ghi của chính mình.
 */
import { useEffect, useState } from "react";
import { RubricResult } from "@/components/RubricView";
import { Button, Card, Space, Tag, Typography, message } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, SoundOutlined } from "@ant-design/icons";

import type { AnswerSnapshot, Question } from "@/api/assessment";
import { fetchAnswerAudioUrl } from "@/api/assessment";
import { MarkdownContent } from "@/components/MarkdownContent";
import { ListenSampleButton } from "@/components/ListenSampleButton";
import { ScoreTag, pctTo10 } from "@/components/ScoreTag";

const { Text, Paragraph } = Typography;

export function PronunciationReview({
  index,
  question,
  answer,
  allowSample = false,
}: {
  index: number;
  question: Question;
  answer?: AnswerSnapshot;
  /** Bài tập → hiện "Nghe mẫu" để so với bản ghi của mình. */
  allowSample?: boolean;
}) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const hasAudio = !!answer?.has_audio;
  const aiScore = answer?.pronunciation_score;
  // Sếp chốt 2026-10-03 — bài kiểm tra: điểm AI chỉ là gợi ý → chờ GV chốt (`is_final`).
  const graded =
    answer?.is_final ??
    (answer?.manually_graded === true || (aiScore !== null && aiScore !== undefined));
  // Điểm câu thang 10 = điểm đạt / điểm tối đa (GV ghi đè → theo points_awarded).
  const score10 =
    question.points > 0 ? pctTo10(((answer?.points_awarded ?? 0) * 100) / question.points) : 0;

  const play = async () => {
    if (!answer?.answer_id) return;
    setLoadingAudio(true);
    try {
      setAudioUrl(await fetchAnswerAudioUrl(answer.answer_id));
    } catch {
      message.error("Không tải được bản ghi.");
    } finally {
      setLoadingAudio(false);
    }
  };

  let status;
  if (!hasAudio) {
    status = <Tag color="default">Chưa ghi âm (0 điểm)</Tag>;
  } else if (graded) {
    status = (
      <Tag color="success" icon={<CheckCircleOutlined />}>
        Đã chấm (+{answer?.points_awarded ?? 0} điểm){answer?.graded_by_ai ? " · AI chấm" : ""}
      </Tag>
    );
  } else {
    status = (
      <Tag color="warning" icon={<ClockCircleOutlined />}>
        Đang chấm phát âm…
      </Tag>
    );
  }

  return (
    <Card
      style={{ width: "100%" }}
      data-testid="pron-review"
      title={
        <Space>
          <Text strong>Câu {index + 1}</Text>
          {status}
        </Space>
      }
    >
      <MarkdownContent source={question.prompt} />
      <Paragraph style={{ fontSize: 18, fontWeight: 600, marginTop: 8 }}>
        {question.speaking_target}
      </Paragraph>
      {allowSample && question.speaking_language && (
        <div style={{ marginBottom: 8 }}>
          <ListenSampleButton
            text={question.speaking_target ?? ""}
            language={question.speaking_language}
            size="small"
          />
        </div>
      )}
      {hasAudio && graded && (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Space>
            <Text type="secondary">Điểm phát âm:</Text>
            <ScoreTag value={score10} />
          </Space>
          {answer?.text_answer && (
            <Text>
              <Text type="secondary">AI nghe được: </Text>
              {answer.text_answer}
            </Text>
          )}
          {question.rubric && answer?.rubric_result ? (
            <RubricResult rubric={question.rubric} result={answer.rubric_result} />
          ) : (
            answer?.feedback && (
              <Text>
                <Text type="secondary">Nhận xét: </Text>
                {answer.feedback}
              </Text>
            )
          )}
        </Space>
      )}
      {hasAudio && !graded && (
        <Text type="secondary">
          Kết quả sẽ hiện sau ít phút (bài kiểm tra: sau khi giảng viên chốt điểm). Nếu AI không
          chấm được, giảng viên sẽ nghe bản ghi và chấm.
        </Text>
      )}
      {hasAudio && (
        <div style={{ marginTop: 12 }}>
          {audioUrl ? (
            <audio controls src={audioUrl} style={{ width: "100%" }} />
          ) : (
            <Button size="small" icon={<SoundOutlined />} loading={loadingAudio} onClick={play}>
              Nghe lại bản ghi của bạn
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
